/**
 * @i18n-channel model
 *
 * The `label`, `description`, and `guidance` strings in this file are read by
 * the MODEL, not by an operator. Two transports carry them, which is why the
 * marker says `model` and not `prompt`:
 *
 *   1. The prompt. `formatStudioChatOperationInstructions` (below) serializes
 *      them into `chatOperationInstructions`, which `chat-model-adapter.ts:500`
 *      and `gateway-chat-service.ts:80` put on the wire.
 *   2. The WebMCP capability tool response, which carries the operation
 *      contract itself — `studio-capability-gateway.ts:4704` and :4717.
 *
 * Translating them would change what the model reads.
 *
 * The operator-facing copy for these operations lives in
 * `studio-operation-display-copy.ts`, keyed by operation id, and the render
 * sinks resolve it there.
 *
 * The marker above is a CLAIM, and it is backed by a gate rather than by trust:
 * `__tests__/studio-chat-operation-prompt-invariance.test.ts` byte-compares all
 * 93 operations against a recorded baseline and fails on any non-ASCII in the
 * prompt lines, and `__tests__/i18n-channel-marker.test.ts` asserts that every
 * file carrying this marker is one whose strings actually appear in that
 * baseline. Do not copy this marker to a file without such a gate — on its own
 * it is a silencer.
 */
import type { ChatContext, ChatOperationContract } from "@/store/chat-types";
import {
  buildStudioOperationV2,
  type StudioContextFrame,
  type StudioOperationLegacyFields,
  type StudioOperationV2,
  type StudioTargetRef,
} from "./studio-operation-v2";
import {
  STUDIO_OPERATION_V2_PROFILES,
  type StudioOperationId,
} from "./studio-operation-v2-profiles";

const REVIEW_CONTEXT_TOOL = "cogo.nav.currentContext";

const LEGACY_OPERATIONS = {
  "workspace.semantic-sync": {
    id: "workspace.semantic-sync",
    label: "Sync one change everywhere",
    kind: "semantic-sync",
    surfaceId: "preview",
    status: "ready",
    webMcpToolName: "cogo.studio.capability.executeOperation",
    studioMcpToolName: "cogo.studio.capability.executeOperation",
    requiresConfirmation: true,
    description:
      'Apply one exact change set to App, Design, and Guide, verify every target in Status, then prepare one immutable publication candidate.',
    guidance:
      "Require exact page and document node IDs plus the approved App title, primary color, and Guide text. Show one combined impact preview and require PUBLISH. A candidate receipt is not a production deployment claim.",
    inputHint: {
      changeLabel: "<bounded semantic change>",
      pageId: "<exact application page id>",
      appTitle: "<approved application title>",
      primaryColor: "<#RRGGBB>",
      documentNodeId: "<exact Guide document node id>",
      guideText: "<approved Guide text>",
    },
  },
  "page.details.review": {
    id: "page.details.review",
    label: "Review page",
    kind: "page-detail",
    surfaceId: "page-inspector",
    status: "ready",
    webMcpToolName: REVIEW_CONTEXT_TOOL,
    readOnly: true,
    requiresConfirmation: false,
    description: "Review the selected page structure before editing.",
    guidance:
      "Inspect the active page first, then propose page metadata or block changes through WebMCP tools.",
  },
  "page.details.rename": {
    id: "page.details.rename",
    label: "Rename page",
    kind: "page-detail",
    surfaceId: "page-inspector",
    status: "ready",
    webMcpToolName: "cogo.studio.updatePageMeta",
    studioMcpToolName: "cogo.studio.updatePageMeta",
    requiresConfirmation: true,
    description:
      "Update a page title or description through the Studio MCP page metadata tool.",
    inputHint: { pageId: "<active page id>", title: "<new title>" },
  },
  "page.details.route": {
    id: "page.details.route",
    label: "Change route",
    kind: "page-detail",
    surfaceId: "page-inspector",
    status: "ready",
    webMcpToolName: "cogo.studio.updatePageMeta",
    studioMcpToolName: "cogo.studio.updatePageMeta",
    requiresConfirmation: true,
    description: "Update the route for the selected page.",
    inputHint: { pageId: "<active page id>", route: "/new-route" },
  },
  "page.details.blocks": {
    id: "page.details.blocks",
    label: "Review blocks",
    kind: "page-detail",
    surfaceId: "page-inspector",
    status: "ready",
    webMcpToolName: "cogo.studio.page.updateBlocks",
    studioMcpToolName: "cogo.studio.page.updateBlocks",
    requiresConfirmation: true,
    description:
      "Replace, append, remove, or move blocks on the selected page.",
    inputHint: {
      pageId: "<active page id>",
      operation: "<replace|append|remove|move>",
      blockNames: ["<block name>"],
    },
  },
  "page.details.layout": {
    id: "page.details.layout",
    label: "Adjust layout",
    kind: "page-detail",
    surfaceId: "page-inspector",
    status: "ready",
    webMcpToolName: "cogo.studio.updatePageMeta",
    studioMcpToolName: "cogo.studio.updatePageMeta",
    requiresConfirmation: true,
    description: "Update the selected page layout metadata.",
    inputHint: { pageId: "<active page id>", layout: "flow" },
  },
  "page.details.delete": {
    id: "page.details.delete",
    label: "Delete page",
    kind: "page-detail",
    surfaceId: "page-inspector",
    status: "ready",
    webMcpToolName: "cogo.studio.deletePage",
    studioMcpToolName: "cogo.studio.deletePage",
    requiresConfirmation: true,
    description:
      "Delete one non-final application page after a typed confirmation and verify that both editor and project authority no longer contain it.",
    guidance:
      "Show the exact page snapshot first. Require DELETE, then retain the post-delete authority readback and undo boundary.",
    inputHint: { pageId: "<active page id>" },
  },
  "element.add": {
    id: "element.add",
    label: "Add element",
    kind: "page-element",
    surfaceId: "page-inspector",
    status: "ready",
    webMcpToolName: "cogo.studio.addElement",
    studioMcpToolName: "cogo.studio.addElement",
    requiresConfirmation: true,
    description:
      "Add an explicitly requested element to the active page block through Studio MCP.",
    guidance:
      "Require an explicit component type and a resolved active block. Do not invent copy, properties, or a parent element.",
    inputHint: {
      blockName: "<active block name>",
      component_type: "<text|button|input|container|...>",
      parentId: "<optional resolved parent element id>",
      properties: {},
    },
  },
  "element.update": {
    id: "element.update",
    label: "Update element",
    kind: "page-element",
    surfaceId: "page-inspector",
    status: "ready",
    webMcpToolName: "cogo.studio.updateElement",
    studioMcpToolName: "cogo.studio.updateElement",
    requiresConfirmation: true,
    description:
      "Update explicit properties on the selected page element through Studio MCP.",
    guidance:
      "Require a selected element and explicit changed values. If either is missing, ask the user instead of choosing the first matching element.",
    inputHint: {
      blockName: "<selected block name>",
      elementId: "<selected element id>",
      changes: {},
    },
  },
  "element.remove": {
    id: "element.remove",
    label: "Remove element",
    kind: "page-element",
    surfaceId: "page-inspector",
    status: "ready",
    webMcpToolName: "cogo.studio.removeElement",
    studioMcpToolName: "cogo.studio.removeElement",
    requiresConfirmation: true,
    description:
      "Remove the selected page element while retaining its subtree snapshot for undo.",
    guidance:
      "Require an unambiguous selected element. Never remove the first element of a matching type by inference.",
    inputHint: {
      blockName: "<selected block name>",
      elementId: "<selected element id>",
    },
  },
  "behavior.surface.review": {
    id: "behavior.surface.review",
    label: "Review behavior",
    kind: "actionflow",
    surfaceId: "behavior",
    status: "ready",
    webMcpToolName: REVIEW_CONTEXT_TOOL,
    readOnly: true,
    requiresConfirmation: false,
    description: "Review selected UI events, flows, and action bindings.",
  },
  "behavior.flow.review": {
    id: "behavior.flow.review",
    label: "Review flow",
    kind: "actionflow",
    surfaceId: "behavior",
    status: "ready",
    webMcpToolName: REVIEW_CONTEXT_TOOL,
    readOnly: true,
    requiresConfirmation: false,
    description: "Inspect the selected ActionFlow before editing steps.",
  },
  "flow.create": {
    id: "flow.create",
    label: "Create flow",
    kind: "actionflow",
    surfaceId: "behavior",
    status: "ready",
    webMcpToolName: "cogo.studio.addFlow",
    studioMcpToolName: "cogo.studio.addFlow",
    requiresConfirmation: true,
    description: "Create a named ActionFlow through Studio MCP.",
    guidance:
      "Require an explicit non-reserved flow name. Do not manufacture a generic flow name when the request omits one.",
    inputHint: {
      name: "<explicit flow name>",
      trigger: "<event|lifecycle|init|manual>",
      preserveSurface: true,
    },
  },
  "flow.rename": {
    id: "flow.rename",
    label: "Rename flow",
    kind: "actionflow",
    surfaceId: "behavior",
    status: "ready",
    webMcpToolName: "cogo.studio.renameFlow",
    studioMcpToolName: "cogo.studio.renameFlow",
    requiresConfirmation: true,
    description:
      "Rename the selected ActionFlow and rewrite its declared keys and event bindings.",
    guidance:
      "Require a resolved flow ID and an explicit new name. Never infer which flow should be renamed.",
    inputHint: {
      flowId: "<selected flow id>",
      blockName: "<optional owning block name>",
      newName: "<explicit new flow name>",
    },
  },
  "behavior.flow.add-step": {
    id: "behavior.flow.add-step",
    label: "Add flow step",
    kind: "actionflow",
    surfaceId: "behavior",
    status: "ready",
    webMcpToolName: "cogo.studio.editFlowSteps",
    studioMcpToolName: "cogo.studio.editFlowSteps",
    requiresConfirmation: true,
    description: "Apply a step-buffer mutation to the selected ActionFlow.",
    inputHint: { flowId: "<selected flow id>", op: "addStep", args: {} },
  },
  "behavior.flow.connect-selected": {
    id: "behavior.flow.connect-selected",
    label: "Connect selected element",
    kind: "actionflow",
    surfaceId: "behavior",
    status: "ready",
    webMcpToolName: "cogo.studio.addEventBinding",
    studioMcpToolName: "cogo.studio.addEventBinding",
    requiresConfirmation: true,
    description:
      "Bind the selected page element event to an ActionFlow through WebMCP.",
    inputHint: {
      blockName: "<selected block>",
      eventKey: "<element id>:click",
      flow: "<flow name>",
      element: {
        elementId: "<selected element id>",
        eventType: "click",
        binding: { flow: "<flow name>" },
      },
    },
  },
  "event-binding.remove": {
    id: "event-binding.remove",
    label: "Remove event binding",
    kind: "actionflow",
    surfaceId: "behavior",
    status: "ready",
    webMcpToolName: "cogo.studio.removeEventBinding",
    studioMcpToolName: "cogo.studio.removeEventBinding",
    requiresConfirmation: true,
    description:
      "Remove a resolved block event binding and its optional element-level copy through one undoable Studio MCP operation.",
    guidance:
      "Require the owning block, exact event key, and selected element when removing a dual-representation binding. Do not guess an event type.",
    inputHint: {
      blockName: "<selected block name>",
      eventKey: "<selected element id>:<explicit event type>",
      element: {
        elementId: "<selected element id>",
        eventType: "<click|change|submit|...>",
      },
    },
  },
  "behavior.flow.test": {
    id: "behavior.flow.test",
    label: "Test flow",
    kind: "actionflow",
    surfaceId: "behavior",
    status: "ready",
    webMcpToolName: "cogo.studio.requestValidation",
    studioMcpToolName: "cogo.studio.requestValidation",
    requiresConfirmation: false,
    description: "Run project/block validation after an ActionFlow change.",
    inputHint: { blockName: "<optional block name>" },
  },
  "behavior.flow.delete": {
    id: "behavior.flow.delete",
    label: "Delete flow",
    kind: "actionflow",
    surfaceId: "behavior",
    status: "ready",
    webMcpToolName: "cogo.studio.deleteFlow",
    studioMcpToolName: "cogo.studio.deleteFlow",
    requiresConfirmation: true,
    description:
      "Delete one ActionFlow and its persisted bindings after a typed confirmation.",
    guidance:
      "Show the flow declaration and owning block first. Require DELETE, verify editor/project absence, and preserve the existing restoreFlow undo snapshot.",
    inputHint: {
      flowId: "<selected flow id>",
      blockName: "<owning block name>",
    },
  },
  "data.surface.review": {
    id: "data.surface.review",
    label: "Review data",
    kind: "data-source",
    surfaceId: "data",
    status: "ready",
    webMcpToolName: REVIEW_CONTEXT_TOOL,
    readOnly: true,
    requiresConfirmation: false,
    description:
      "Review variables, datasources, events, and flow/data relationships.",
  },
  "evidence.checks.review": {
    id: "evidence.checks.review",
    label: "Review checks",
    kind: "verification-evidence",
    surfaceId: "evidence",
    status: "ready",
    webMcpToolName: REVIEW_CONTEXT_TOOL,
    readOnly: true,
    requiresConfirmation: false,
    description:
      "Review the verification evidence and readiness blockers currently shown in Checks.",
    guidance:
      "Use the bounded Checks handoff as the evidence snapshot. Do not claim that currentContext independently re-ran any proof.",
  },
  "evidence.candidate.read": {
    id: "evidence.candidate.read",
    label: "Read candidate verification",
    kind: "verification-evidence",
    surfaceId: "evidence",
    status: "ready",
    webMcpToolName: "cogo.studio.candidate.readVerification",
    studioMcpToolName: "cogo.studio.candidate.readVerification",
    readOnly: true,
    requiresConfirmation: false,
    description:
      "Read the open project's candidate verification model from its saved descriptor and a freshly re-read orchestration journal.",
    guidance:
      "Treat the Ask button's bounded project, candidate, run, and visible-status context only as a handoff snapshot. Execute this tool for a fresh readback. Only durable-journal verified is verification authority; pending, checking, ready, project metadata, and browser state are not a pass.",
  },
  "evidence.sufficiency.read": {
    id: "evidence.sufficiency.read",
    label: "Read source sufficiency",
    kind: "verification-evidence",
    surfaceId: "evidence",
    status: "ready",
    webMcpToolName: "cogo.studio.ontology.read_source_sufficiency",
    studioMcpToolName: "cogo.studio.ontology.read_source_sufficiency",
    readOnly: true,
    requiresConfirmation: false,
    description:
      "Read the open project's current source sufficiency assessment and deterministic Situation brief from the authenticated BFF.",
    guidance:
      "Do not use browser state, a score, or a model signal as decision authority. Explain only the returned assessment, evidence gate, and explicit follow-up decisions.",
    inputHint: {
      subjectId: "<optional exact persisted source assessment subject>",
    },
  },
  "evidence.sufficiency.answer": {
    id: "evidence.sufficiency.answer",
    label: "Answer source clarification",
    kind: "verification-evidence",
    surfaceId: "evidence",
    status: "ready",
    webMcpToolName: "cogo.studio.ontology.answer_source_clarification",
    studioMcpToolName: "cogo.studio.ontology.answer_source_clarification",
    readOnly: false,
    requiresConfirmation: true,
    description:
      "Save one user-confirmed answer to the next source-sufficiency question, then return the BFF-regenerated immutable assessment and Situation brief.",
    guidance:
      "First read the authenticated BFF assessment. Ask only its next current question, accept exactly one BFF-provided option after user confirmation, then re-read before asking another. Do not send projectId, tenantId, evidence references, browser scores, model signals, or a caller-supplied assessment revision. This changes source-assessment evidence only; it does not change App, Design, Guide, or publish anything.",
    inputHint: {
      subjectId: "<optional exact persisted source assessment subject>",
      clarificationRequestId: "<next BFF clarification question id>",
      decision: "<one exact next BFF-provided option>",
    },
  },
  "evidence.knowledge.search": {
    id: "evidence.knowledge.search",
    label: "Search approved knowledge",
    kind: "application-knowledge",
    surfaceId: "evidence",
    status: "ready",
    webMcpToolName: "cogo.studio.ontology.search_application_knowledge",
    studioMcpToolName: "cogo.studio.ontology.search_application_knowledge",
    readOnly: true,
    requiresConfirmation: false,
    description:
      "Search evidence from the open project's exact current-ready application knowledge release.",
    guidance:
      "Require an explicit query. Treat semantic similarity only as candidate selection, preserve typed exact-lexical fallback, and use only returned release-bound evidence as authority.",
    inputHint: {
      query: "<explicit application knowledge question>",
      maxResults: 8,
      subjectId: "<optional exact selected application-abox subject>",
      target: "<optional cogo-web|cogo-flutter|cogo-app-host>",
    },
  },
  "variable.add": {
    id: "variable.add",
    label: "Add variable",
    kind: "data-variable",
    surfaceId: "data",
    status: "ready",
    webMcpToolName: "cogo.studio.addVariable",
    studioMcpToolName: "cogo.studio.addVariable",
    requiresConfirmation: true,
    description: "Add a typed variable to the active block through Studio MCP.",
    guidance:
      "Require the owning block, variable name, scope, and type. Do not invent a fallback variable name.",
    inputHint: {
      blockName: "<active block name>",
      scope: "<state|computed|context>",
      name: "<explicit variable name>",
      type: "<string|number|boolean|array|object>",
      default: "<optional explicit default>",
    },
  },
  "variable.update": {
    id: "variable.update",
    label: "Update variable",
    kind: "data-variable",
    surfaceId: "data",
    status: "ready",
    webMcpToolName: "cogo.studio.updateVariable",
    studioMcpToolName: "cogo.studio.updateVariable",
    requiresConfirmation: true,
    description:
      "Update explicit fields on an existing block variable through Studio MCP.",
    guidance:
      "Require an exact variable name and at least one explicit changed field. Ask for the missing value instead of issuing a no-op update.",
    inputHint: {
      blockName: "<active block name>",
      scope: "<state|computed|context>",
      name: "<existing variable name>",
      type: "<optional new type>",
      default: "<optional new default>",
    },
  },
  "variable.remove": {
    id: "variable.remove",
    label: "Remove variable",
    kind: "data-variable",
    surfaceId: "data",
    status: "ready",
    webMcpToolName: "cogo.studio.removeVariable",
    studioMcpToolName: "cogo.studio.removeVariable",
    requiresConfirmation: true,
    description:
      "Remove an exact block variable after typed confirmation and retain its definition for undo.",
    guidance:
      "Require the owning block, scope, and exact variable name. Show the affected variable before requesting typed confirmation.",
    inputHint: {
      blockName: "<active block name>",
      scope: "<state|computed|context>",
      name: "<existing variable name>",
    },
  },
  "data.source.add": {
    id: "data.source.add",
    label: "Add data source",
    kind: "data-source",
    surfaceId: "data",
    status: "ready",
    webMcpToolName: "cogo.studio.addDatasource",
    studioMcpToolName: "cogo.studio.addDatasource",
    requiresConfirmation: true,
    description: "Add a datasource to the selected block through Studio MCP.",
    inputHint: {
      blockName: "<selected block>",
      name: "<source name>",
      type: "rest",
    },
  },
  "data.source.scaffold-from-chat": {
    id: "data.source.scaffold-from-chat",
    label: "Scaffold data source",
    kind: "data-source",
    surfaceId: "data",
    status: "ready",
    webMcpToolName: "cogo.studio.addDatasource",
    studioMcpToolName: "cogo.studio.addDatasource",
    requiresConfirmation: true,
    description:
      "Create a block-scoped datasource scaffold from a chat command; unresolved endpoints stay marked needs-configuration.",
    inputHint: {
      blockName: "<selected block>",
      name: "chatDataSource",
      type: "rest",
      status: "needs-configuration",
    },
  },
  "datasource.update": {
    id: "datasource.update",
    label: "Update data source",
    kind: "data-source",
    surfaceId: "data",
    status: "ready",
    webMcpToolName: "cogo.studio.updateDatasource",
    studioMcpToolName: "cogo.studio.updateDatasource",
    requiresConfirmation: true,
    description:
      "Update explicit configuration fields on an existing block datasource through Studio MCP.",
    guidance:
      "Require an exact datasource name and at least one explicit changed field such as URL or method. Never substitute an example endpoint.",
    inputHint: {
      blockName: "<active block name>",
      name: "<existing datasource name>",
      url: "<optional explicit endpoint>",
      method: "<optional explicit method>",
      config: {},
    },
  },
  "data.binding.connect": {
    id: "data.binding.connect",
    label: "Connect data binding",
    kind: "data-binding",
    surfaceId: "data",
    status: "ready",
    webMcpToolName: "cogo.studio.data.bindComponentSource",
    studioMcpToolName: "cogo.studio.data.bindComponentSource",
    requiresConfirmation: true,
    description: "Bind a selected component to a verified block datasource.",
    inputHint: {
      blockName: "<selected block>",
      elementId: "<selected element id>",
      sourceName: "<source name>",
      bindingExpression: "#{appData.items}",
      mode: "collection",
    },
  },
  "data.flow.debug": {
    id: "data.flow.debug",
    label: "Debug data flow",
    kind: "data-source",
    surfaceId: "data",
    status: "ready",
    webMcpToolName: "cogo.studio.refreshDatasource",
    studioMcpToolName: "cogo.studio.refreshDatasource",
    requiresConfirmation: false,
    description: "Refresh or inspect a datasource while debugging a data flow.",
    inputHint: { name: "<source name>" },
  },
  "data.source.remove": {
    id: "data.source.remove",
    label: "Remove data source",
    kind: "data-source",
    surfaceId: "data",
    status: "ready",
    webMcpToolName: "cogo.studio.removeDatasource",
    studioMcpToolName: "cogo.studio.removeDatasource",
    requiresConfirmation: true,
    description:
      "Remove one block-scoped datasource after a typed confirmation and verify its canonical project entry is absent.",
    guidance:
      "Show the exact datasource configuration first. Require DELETE and keep the restoreDatasource undo snapshot.",
    inputHint: {
      blockName: "<selected block>",
      name: "<source name>",
    },
  },
  "application.generate": {
    id: "application.generate",
    label: "Generate application",
    kind: "application-generation",
    surfaceId: "preview",
    status: "ready",
    webMcpToolName: "cogo.studio.requestAppGeneration",
    studioMcpToolName: "cogo.studio.requestAppGeneration",
    requiresConfirmation: true,
    description:
      "Generate a complete application from an explicit app specification through the Studio v2 pipeline.",
    guidance:
      "Require an explicit appSpec with at least one page. Show generation impact before confirmation, and report working-copy/runtime readback separately from server persistence.",
    inputHint: { appSpec: { name: "<app name>", pages: ["<page spec>"] } },
  },
  "application.create-from-brief": {
    id: "application.create-from-brief",
    label: "Create application from brief",
    kind: "application-import",
    surfaceId: "preview",
    status: "ready",
    webMcpToolName: "cogo.studio.create_cogo_app",
    studioMcpToolName: "cogo.studio.create_cogo_app",
    requiresConfirmation: true,
    description:
      "Create a Coogo work item, bundle record, and preview candidate from an explicit brief or source input.",
    guidance:
      "Require workspace ID, title, input type, exact input, and target. Do not infer a missing source body or claim a live preview without readback.",
    inputHint: {
      workspaceId: "<workspace id>",
      title: "<explicit title>",
      sourceType: "assistant_prompt",
      inputType: "prompt",
      input: "<exact brief>",
      target: "app",
    },
  },
  "application.patch": {
    id: "application.patch",
    label: "Apply application patch",
    kind: "cogo-json-mutation",
    surfaceId: "code",
    status: "ready",
    webMcpToolName: "cogo.studio.applyAppPatch",
    studioMcpToolName: "cogo.studio.applyAppPatch",
    requiresConfirmation: true,
    description:
      "Apply explicit RFC6902 add, remove, or replace patches to the active Coogo application.",
    guidance:
      "Require a non-empty explicit patch list. Never invent a JSON pointer or value; keep the returned inverse for undo and state that readback is local until saved.",
    inputHint: {
      patches: [{ op: "replace", path: "/manifest/name", value: "<new name>" }],
    },
  },
  "application.bundle.export": {
    id: "application.bundle.export",
    label: "Export Coogo bundle",
    kind: "cogo-json-export",
    surfaceId: "code",
    status: "ready",
    webMcpToolName: "cogo.studio.export_cogo_bundle",
    studioMcpToolName: "cogo.studio.export_cogo_bundle",
    requiresConfirmation: true,
    description:
      "Export an exact recorded Coogo bundle as a traceable artifact reference.",
    guidance:
      "Require the exact bundle ID and format. Return the artifact reference but do not claim downloadability or external availability without independent readback.",
    inputHint: { bundleId: "<bundle id>", format: "cogo_json" },
  },
  "figma.source.import": {
    id: "figma.source.import",
    label: "Import Figma source",
    kind: "source-import",
    surfaceId: "source",
    status: "ready",
    webMcpToolName: "cogo.studio.import_openpencil_figma_source",
    studioMcpToolName: "cogo.studio.import_openpencil_figma_source",
    requiresConfirmation: true,
    description:
      "Register an exact .fig design source without generating anything.",
    guidance:
      "Require the complete sourceBundle. Do not synthesize source facts from a URL, and keep the previous source-workspace snapshot for undo.",
    inputHint: {
      sourceBundle: "<complete design source bundle>",
      openInSourceSurface: true,
    },
  },
  "figma.token.validate": {
    id: "figma.token.validate",
    label: "Validate Figma token",
    kind: "source-auth",
    surfaceId: "source",
    status: "ready",
    webMcpToolName: "cogo.studio.validate_figma_token",
    studioMcpToolName: "cogo.studio.validate_figma_token",
    requiresConfirmation: true,
    description:
      "Validate and store an explicitly supplied Figma access token through the bounded server control.",
    guidance:
      "Require the token through the secure input path, never echo it, and do not claim durable storage without server readback proof.",
    inputHint: {
      figmaAccessToken: "<secure token>",
      workspaceId: "<optional workspace id>",
    },
  },
  "figma.frame.import": {
    id: "figma.frame.import",
    label: "Import Figma frame",
    kind: "source-import",
    surfaceId: "source",
    status: "ready",
    webMcpToolName: "cogo.studio.import_figma_frame",
    studioMcpToolName: "cogo.studio.import_figma_frame",
    requiresConfirmation: true,
    description:
      "Import one exact Figma file/node pair into a Coogo preview candidate.",
    guidance:
      "Require workspace ID, file ID, node ID, preview choice, a stored token reference, and explicit user template intent/constraints. Never guess a frame from a file URL or derive selection intent from the source itself.",
    inputHint: {
      workspaceId: "<workspace id>",
      figmaFileId: "<file id>",
      figmaNodeId: "<node id>",
      createPreview: true,
      figmaTokenRef: "<stored token reference>",
      templateSelection: {
        userIntent: "<user-stated template intent>",
        scope: "template",
        expectedViewportClass: "<mobile|tablet|desktop|unknown>",
      },
    },
  },
  "github.preview.import": {
    id: "github.preview.import",
    label: "Import GitHub PR preview",
    kind: "application-import",
    surfaceId: "preview",
    status: "ready",
    webMcpToolName: "cogo.studio.import_github_pr_preview",
    studioMcpToolName: "cogo.studio.import_github_pr_preview",
    requiresConfirmation: true,
    description:
      "Read one exact GitHub pull request and create a Coogo preview evidence packet without writing to GitHub.",
    guidance:
      "Require workspace ID, owner/repo, and PR number or an exact GitHub pull-request URL. Do not claim the preview is live without server readback.",
    inputHint: {
      workspaceId: "<workspace id>",
      repo: "owner/repo",
      prNumber: 123,
    },
  },
  "source.figma.inspect": {
    id: "source.figma.inspect",
    label: "Inspect Figma source",
    kind: "source-readback",
    surfaceId: "source",
    status: "ready",
    webMcpToolName: "cogo.studio.inspect_openpencil_source_workspace",
    studioMcpToolName: "cogo.studio.inspect_openpencil_source_workspace",
    readOnly: true,
    requiresConfirmation: false,
    description:
      "Inspect what was read from the design, mapping decisions, visual evidence, and generation gates.",
  },
  "source.figma.export_original": {
    id: "source.figma.export_original",
    label: "Export current Figma source",
    kind: "source-export",
    surfaceId: "source",
    status: "ready",
    webMcpToolName: "cogo.studio.export_openpencil_source_file",
    studioMcpToolName: "cogo.studio.export_openpencil_source_file",
    readOnly: true,
    requiresConfirmation: false,
    description:
      "Download the checksum-verified current retained .fig or .pen source without modifying it.",
  },
  "source.figma.edit": {
    id: "source.figma.edit",
    label: "Edit Figma source",
    kind: "source-edit",
    surfaceId: "source",
    status: "ready",
    webMcpToolName: "cogo.studio.revise_openpencil_figma_source",
    studioMcpToolName: "cogo.studio.revise_openpencil_figma_source",
    requiresConfirmation: true,
    description:
      "Apply bounded node edits, export a new locked .fig revision, and restart the design source checks.",
    guidance:
      "Inspect the source node ID, type, and current name first. Never infer a node ID or bypass confirmation.",
    inputHint: {
      actor: "agent",
      reason: "<confirmed source edit>",
      operations: [
        {
          operation:
            "<rename-node|set-text|set-visible|set-opacity|set-geometry>",
          nodeId: "<verified source node id>",
          expectedNodeType: "<verified node type>",
          expectedName: "<verified current name>",
        },
      ],
    },
  },
  "source.figma.apply_design_mutation": {
    id: "source.figma.apply_design_mutation",
    label: "Apply coordinated design mutation",
    kind: "source-design-mutation",
    surfaceId: "source",
    status: "ready",
    webMcpToolName: "cogo.studio.apply_openpencil_cogo_design_mutation",
    studioMcpToolName: "cogo.studio.apply_openpencil_cogo_design_mutation",
    requiresConfirmation: true,
    description:
      "Apply one approved change to a locked design revision and its exact Coogo JSON element targets.",
    guidance:
      "Use only a content-addressed cogo.design-mutation.v1 value whose source revision, Coogo project hash, node IDs, element IDs, and expected values were read back immediately before confirmation.",
    inputHint: {
      proposal: "<frozen cogo.design-mutation-proposal.v1>",
    },
  },
  "source.figma.propose_design_mutation": {
    id: "source.figma.propose_design_mutation",
    label: "Prepare app + source edit",
    kind: "source-design-mutation-proposal",
    surfaceId: "source",
    status: "ready",
    webMcpToolName: "cogo.studio.propose_openpencil_cogo_design_mutation",
    studioMcpToolName: "cogo.studio.propose_openpencil_cogo_design_mutation",
    readOnly: true,
    requiresConfirmation: false,
    description:
      "Read exact source and Coogo targets and prepare a content-addressed mutation proposal without changing either authority.",
    guidance:
      "Use verified node, block, and element IDs. After reviewing the returned before/after paths, pass its frozen proposal to source.figma.apply_design_mutation for user confirmation.",
    inputHint: {
      reason: "<requested bounded edit>",
      operations: [
        {
          operationId: "<stable operation id>",
          kind: "<rename-node|set-text|set-visible|set-opacity|set-geometry>",
          sourceTarget: {
            nodeId: "<verified source node id>",
            expectedNodeType: "<verified source node type>",
          },
          cogoTarget: {
            blockName: "<verified block name>",
            elementId: "<verified element id>",
          },
          nextValue: "<bounded next value>",
        },
      ],
    },
  },
  "source.figma.apply_mapping": {
    id: "source.figma.apply_mapping",
    label: "Apply source mapping",
    kind: "source-mapping",
    surfaceId: "source",
    status: "ready",
    webMcpToolName: "cogo.studio.apply_openpencil_suggested_mapping",
    studioMcpToolName: "cogo.studio.apply_openpencil_suggested_mapping",
    requiresConfirmation: true,
    description:
      "Accept geometry-derived page, story, and screen-role suggestions as one reversible source-workspace revision.",
    inputHint: { actor: "agent", reason: "<confirmed mapping decision>" },
  },
  "source.figma.decide_tokens": {
    id: "source.figma.decide_tokens",
    label: "Decide design tokens",
    kind: "source-token",
    surfaceId: "source",
    status: "ready",
    webMcpToolName: "cogo.studio.set_all_openpencil_token_decisions",
    studioMcpToolName: "cogo.studio.set_all_openpencil_token_decisions",
    requiresConfirmation: true,
    description:
      "Accept or reject the current design-token candidates as a reversible revision.",
    inputHint: { status: "<accepted|rejected>", actor: "agent" },
  },
  "source.figma.visual_review": {
    id: "source.figma.visual_review",
    label: "Run visual review",
    kind: "source-visual-evidence",
    surfaceId: "source",
    status: "ready",
    webMcpToolName: "cogo.studio.request_openpencil_visual_judgement",
    studioMcpToolName: "cogo.studio.request_openpencil_visual_judgement",
    requiresConfirmation: true,
    description:
      "Queue the bounded Gemma vision judge against verified R2 previews and retain its evidence without writing application JSON.",
    inputHint: { surfaceIds: ["<accepted screen ids; omit for all>"] },
  },
  "source.figma.assemble": {
    id: "source.figma.assemble",
    label: "Build source assembly",
    kind: "source-assembly",
    surfaceId: "source",
    status: "ready",
    webMcpToolName: "cogo.studio.build_openpencil_source_assembly",
    studioMcpToolName: "cogo.studio.build_openpencil_source_assembly",
    requiresConfirmation: true,
    description:
      "Build the deterministic selected-source assembly manifest after mapping, token, and visual gates pass.",
    inputHint: { evidenceRefs: ["<optional planning evidence ref>"] },
  },
  "source.figma.runtime_proof": {
    id: "source.figma.runtime_proof",
    label: "Run runtime proof",
    kind: "source-runtime-proof",
    surfaceId: "source",
    status: "ready",
    webMcpToolName: "cogo.studio.request_openpencil_runtime_proof",
    studioMcpToolName: "cogo.studio.request_openpencil_runtime_proof",
    requiresConfirmation: true,
    description:
      "Queue server-side Playwright proof for the generated candidate preview and retain desktop, mobile, console, interaction, and source-canary evidence.",
    inputHint: {},
  },
  "source.figma.create_candidate": {
    id: "source.figma.create_candidate",
    label: "Create Coogo candidate",
    kind: "source-cogo-candidate",
    surfaceId: "source",
    status: "ready",
    webMcpToolName: "cogo.studio.create_openpencil_cogo_candidate",
    studioMcpToolName: "cogo.studio.create_openpencil_cogo_candidate",
    requiresConfirmation: true,
    description:
      "Import deterministic Coogo JSON only when its source metadata and page/surface coverage exactly match the validated design assembly.",
    inputHint: {
      workspaceId: "<current workspace id>",
      title: "<candidate title>",
      input: "<deterministic Coogo JSON with sourceAuthority metadata>",
      target: "app",
    },
  },
  "report.workspace.review": {
    id: "report.workspace.review",
    label: "Review report workspace",
    kind: "report-workspace",
    surfaceId: "report-workspace",
    status: "ready",
    webMcpToolName: REVIEW_CONTEXT_TOOL,
    readOnly: true,
    requiresConfirmation: false,
    description:
      "Review report workspace evidence, document pages, and export readiness.",
  },
  "report.document-page.review": {
    id: "report.document-page.review",
    label: "Review document page",
    kind: "document-page",
    surfaceId: "report-workspace",
    status: "ready",
    webMcpToolName: REVIEW_CONTEXT_TOOL,
    readOnly: true,
    requiresConfirmation: false,
    description: "Review one document page and its source coverage.",
  },
  "report.document-page.edit": {
    id: "report.document-page.edit",
    label: "Edit document page",
    kind: "document-page",
    surfaceId: "report-workspace",
    status: "ready",
    webMcpToolName: "cogo.studio.applyReportSemanticEdit",
    studioMcpToolName: "cogo.studio.applyReportSemanticEdit",
    requiresConfirmation: true,
    description: "Apply a semantic edit to report/document workspace metadata.",
    inputHint: {
      replacementText: "<requested edit>",
      target: "<pdf|docx|pptx|xlsx>",
    },
  },
  "report.document-page.validate": {
    id: "report.document-page.validate",
    label: "Validate document page",
    kind: "document-page",
    surfaceId: "report-workspace",
    status: "ready",
    webMcpToolName: "cogo.studio.requestValidation",
    studioMcpToolName: "cogo.studio.requestValidation",
    requiresConfirmation: false,
    description:
      "Run validation for the document-page-backed report workspace.",
  },
  "report.document-page.export": {
    id: "report.document-page.export",
    label: "Export document page",
    kind: "document-page",
    surfaceId: "report-workspace",
    status: "ready",
    webMcpToolName: "cogo.studio.document.export_page",
    studioMcpToolName: "cogo.studio.document.export_page",
    requiresConfirmation: true,
    description:
      "Request a page-scoped export and keep the request blocked until the exported file has been checked.",
    inputHint: {
      pageId: "<selected document page id>",
      target: "<docx|hwpx|pptx|pdf|xlsx>",
    },
  },
  "report.export-target.validate": {
    id: "report.export-target.validate",
    label: "Validate export target",
    kind: "export-target",
    surfaceId: "report-workspace",
    status: "ready",
    webMcpToolName: "cogo.studio.requestValidation",
    studioMcpToolName: "cogo.studio.requestValidation",
    requiresConfirmation: false,
    description: "Validate the selected native export target readiness.",
  },
  "document.workspace.inspect": {
    id: "document.workspace.inspect",
    label: "Inspect document workspace",
    kind: "document-workspace",
    surfaceId: "document-workspace",
    status: "ready",
    webMcpToolName: "cogo.studio.document.inspect",
    studioMcpToolName: "cogo.studio.document.inspect",
    readOnly: true,
    requiresConfirmation: false,
    description:
      "Inspect the document workspace, page outline, proposal queue, and export/check state.",
  },
  "document.page.inspect": {
    id: "document.page.inspect",
    label: "Inspect document page",
    kind: "document-page",
    surfaceId: "document-workspace",
    status: "ready",
    webMcpToolName: "cogo.studio.document.inspect_page",
    studioMcpToolName: "cogo.studio.document.inspect_page",
    readOnly: true,
    requiresConfirmation: false,
    description: "Inspect one document page before editing or exporting.",
  },
  "document.page.create": {
    id: "document.page.create",
    label: "Create document page",
    kind: "document-page",
    surfaceId: "document-workspace",
    status: "ready",
    webMcpToolName: "cogo.studio.document.create_page",
    studioMcpToolName: "cogo.studio.document.create_page",
    requiresConfirmation: true,
    description:
      "Create an ordered page or slide in the canonical document graph.",
    inputHint: { title: "<page title>", kind: "page" },
  },
  "document.page.update_node": {
    id: "document.page.update_node",
    label: "Update document content",
    kind: "document-page",
    surfaceId: "document-workspace",
    status: "ready",
    webMcpToolName: "cogo.studio.document.update_node",
    studioMcpToolName: "cogo.studio.document.update_node",
    requiresConfirmation: true,
    description:
      "Update text, title, or semantic attributes on one document node.",
    inputHint: { nodeId: "<selected node id>", text: "<replacement text>" },
  },
  "document.page.wordgard": {
    id: "document.page.wordgard",
    label: "Open narrative draft",
    kind: "document-page",
    surfaceId: "document-workspace",
    status: "ready",
    webMcpToolName: "cogo.studio.document.wordgard_page",
    studioMcpToolName: "cogo.studio.document.wordgard_page",
    readOnly: true,
    requiresConfirmation: false,
    description:
      "Project a DOCX/HWPX narrative page into the bounded WordGard collaboration adapter.",
    inputHint: { pageId: "<selected page id>", target: "docx" },
  },
  "document.page.move": {
    id: "document.page.move",
    label: "Move document page",
    kind: "document-page",
    surfaceId: "document-workspace",
    status: "ready",
    webMcpToolName: "cogo.studio.document.move_page",
    studioMcpToolName: "cogo.studio.document.move_page",
    requiresConfirmation: true,
    description: "Move a document page to a new ordered position.",
    inputHint: { pageId: "<selected page id>", toIndex: 0 },
  },
  "document.page.delete": {
    id: "document.page.delete",
    label: "Delete document page",
    kind: "document-page",
    surfaceId: "document-workspace",
    status: "ready",
    webMcpToolName: "cogo.studio.document.delete_page",
    studioMcpToolName: "cogo.studio.document.delete_page",
    requiresConfirmation: true,
    description:
      "Delete a document page and its descendants after confirmation.",
    inputHint: { pageId: "<selected page id>" },
  },
  "document.page.export": {
    id: "document.page.export",
    label: "Export document page",
    kind: "document-export",
    surfaceId: "document-workspace",
    status: "ready",
    webMcpToolName: "cogo.studio.document.export_page",
    studioMcpToolName: "cogo.studio.document.export_page",
    requiresConfirmation: true,
    description: "Request an export for one selected document page.",
    inputHint: {
      pageId: "<selected document page id>",
      target: "<docx|hwpx|pptx|pdf|xlsx>",
    },
  },
  "document.page.select_anchor": {
    id: "document.page.select_anchor",
    label: "Select document anchor",
    kind: "document-page",
    surfaceId: "document-workspace",
    status: "ready",
    webMcpToolName: "cogo.studio.document.select_anchor",
    studioMcpToolName: "cogo.studio.document.select_anchor",
    readOnly: true,
    requiresConfirmation: false,
    description:
      "Select a document node or preview anchor before proposal, export, or checking.",
    inputHint: {
      documentNodeId: "<selected document node id>",
      target: "<docx|hwpx|pptx|pdf|xlsx>",
    },
  },
  "document.page.propose_edit": {
    id: "document.page.propose_edit",
    label: "Propose document edit",
    kind: "document-page",
    surfaceId: "document-workspace",
    status: "ready",
    webMcpToolName: "cogo.studio.document.propose_edit",
    studioMcpToolName: "cogo.studio.document.propose_edit",
    requiresConfirmation: true,
    description:
      "Create a reviewable document edit proposal without directly patching Coogo JSON.",
    inputHint: {
      documentNodeId: "<selected document node id>",
      documentNodeKind: "paragraph",
      target: "<docx|hwpx|pptx|pdf|xlsx>",
      body: "<requested edit>",
    },
  },
  "document.proposal.apply": {
    id: "document.proposal.apply",
    label: "Apply document proposal",
    kind: "document-proposal",
    surfaceId: "document-workspace",
    status: "ready",
    webMcpToolName: "cogo.studio.document.apply_edit",
    studioMcpToolName: "cogo.studio.document.apply_edit",
    requiresConfirmation: true,
    description:
      "Apply an approved document proposal as a replayable Coogo document operation.",
    inputHint: { proposalId: "<proposal id>" },
  },
  "document.proposal.list": {
    id: "document.proposal.list",
    label: "List document proposals",
    kind: "document-proposal",
    surfaceId: "document-workspace",
    status: "ready",
    webMcpToolName: "cogo.studio.document.list_proposals",
    studioMcpToolName: "cogo.studio.document.list_proposals",
    readOnly: true,
    requiresConfirmation: false,
    description:
      "List document proposals, their statuses, conflicts, and blockers.",
  },
  "document.proposal.reject": {
    id: "document.proposal.reject",
    label: "Reject document proposal",
    kind: "document-proposal",
    surfaceId: "document-workspace",
    status: "ready",
    webMcpToolName: "cogo.studio.document.reject_edit",
    studioMcpToolName: "cogo.studio.document.reject_edit",
    requiresConfirmation: true,
    description:
      "Reject a document proposal while preserving the proposal history.",
    inputHint: { proposalId: "<proposal id>" },
  },
  "document.blocker.explain": {
    id: "document.blocker.explain",
    label: "Explain document blocker",
    kind: "document-blocker",
    surfaceId: "document-workspace",
    status: "ready",
    webMcpToolName: "cogo.studio.document.explain_blocker",
    studioMcpToolName: "cogo.studio.document.explain_blocker",
    readOnly: true,
    requiresConfirmation: false,
    description:
      "Explain why a document proposal, export target, or check is blocked.",
    inputHint: {
      proposalId: "<optional proposal id>",
      target: "<optional docx|hwpx|pptx|pdf|xlsx>",
    },
  },
  "document.target.export": {
    id: "document.target.export",
    label: "Export document target",
    kind: "document-export",
    surfaceId: "document-workspace",
    status: "ready",
    webMcpToolName: "cogo.studio.document.export",
    studioMcpToolName: "cogo.studio.document.export",
    requiresConfirmation: true,
    description:
      "Record a target-specific document export request and keep it blocked until the exported file has been checked.",
    inputHint: { target: "<docx|hwpx|pptx|pdf|xlsx>" },
  },
  "document.target.readback": {
    id: "document.target.readback",
    label: "Inspect document export checks",
    kind: "document-readback",
    surfaceId: "document-workspace",
    status: "ready",
    webMcpToolName: "cogo.studio.document.readback",
    studioMcpToolName: "cogo.studio.document.readback",
    readOnly: true,
    requiresConfirmation: false,
    description:
      "Inspect export and check readiness for one document target without claiming readiness early.",
    inputHint: { target: "<docx|hwpx|pptx|pdf|xlsx>" },
  },
  "theme.preset.set": {
    id: "theme.preset.set",
    label: "Set theme preset",
    kind: "theme",
    surfaceId: "settings",
    status: "ready",
    webMcpToolName: "cogo.studio.setThemePreset",
    studioMcpToolName: "cogo.studio.setThemePreset",
    requiresConfirmation: true,
    description:
      "Apply one named generated-application theme preset after explicit confirmation.",
    guidance:
      "Use only a known preset ID. Verify local design tokens and preview state; do not claim server persistence.",
    inputHint: { presetId: "<modern|warm|professional|vibrant|minimal>" },
  },
  "theme.color.update": {
    id: "theme.color.update",
    label: "Update theme color",
    kind: "theme",
    surfaceId: "settings",
    status: "ready",
    webMcpToolName: "cogo.studio.updateThemeColor",
    studioMcpToolName: "cogo.studio.updateThemeColor",
    requiresConfirmation: true,
    description:
      "Update one generated-application design color token after explicit confirmation.",
    guidance:
      "Require an exact token key and color value. Verify the local design tokens and preview state; do not infer either value.",
    inputHint: { key: "primary", value: "#4F46E5" },
  },
  "theme.font.set": {
    id: "theme.font.set",
    label: "Set application font",
    kind: "theme",
    surfaceId: "settings",
    status: "ready",
    webMcpToolName: "cogo.studio.setFont",
    studioMcpToolName: "cogo.studio.setFont",
    requiresConfirmation: true,
    description:
      "Set the generated application's font family after explicit confirmation.",
    guidance:
      "Require the exact font-family value and verify the local preview. Do not claim that a remote font asset was provisioned.",
    inputHint: { fontFamily: "Pretendard" },
  },
  "theme.radius.set": {
    id: "theme.radius.set",
    label: "Set application radius",
    kind: "theme",
    surfaceId: "settings",
    status: "ready",
    webMcpToolName: "cogo.studio.setRadius",
    studioMcpToolName: "cogo.studio.setRadius",
    requiresConfirmation: true,
    description:
      "Set the generated application's border-radius scale after explicit confirmation.",
    guidance:
      "Use only none, sm, md, or lg and verify the local design-token readback.",
    inputHint: { radius: "<none|sm|md|lg>" },
  },
  "theme.reset": {
    id: "theme.reset",
    label: "Reset application theme",
    kind: "theme",
    surfaceId: "settings",
    status: "ready",
    webMcpToolName: "cogo.studio.resetTheme",
    studioMcpToolName: "cogo.studio.resetTheme",
    requiresConfirmation: true,
    description:
      "Reset generated-application design tokens to the Modern preset after explicit confirmation.",
    guidance:
      "Explain that reset replaces the local application design tokens. The current tool has no exact automated inverse.",
    inputHint: {},
  },
  "project.create": {
    id: "project.create",
    label: "Create project",
    kind: "project",
    surfaceId: "preview",
    status: "ready",
    webMcpToolName: "cogo.studio.createProject",
    studioMcpToolName: "cogo.studio.createProject",
    requiresConfirmation: true,
    description:
      "Create a server-backed Studio project with an explicit name and optional template ID.",
    guidance:
      "Require the exact project name. A local project-list update is not a server acknowledgement or reconnect readback.",
    inputHint: { name: "<project name>", templateId: "<optional template id>" },
  },
  "project.open": {
    id: "project.open",
    label: "Open project",
    kind: "project",
    surfaceId: "preview",
    status: "ready",
    webMcpToolName: "cogo.studio.openProject",
    studioMcpToolName: "cogo.studio.openProject",
    readOnly: true,
    requiresConfirmation: false,
    description: "Open one Studio project by its exact project ID.",
    guidance:
      "Require an exact project ID. If the ID is unavailable, ask the user to select a project instead of guessing from its name.",
    inputHint: { projectId: "<exact project id>" },
  },
  "project.close": {
    id: "project.close",
    label: "Close project",
    kind: "project",
    surfaceId: "preview",
    status: "ready",
    webMcpToolName: "cogo.studio.closeProject",
    studioMcpToolName: "cogo.studio.closeProject",
    readOnly: true,
    requiresConfirmation: false,
    description:
      "Close the active project in Studio without deleting or publishing it.",
    guidance:
      "Close only the active local workspace. Do not describe this navigation action as a server mutation.",
    inputHint: {},
  },
  "project.rename": {
    id: "project.rename",
    label: "Rename project",
    kind: "project",
    surfaceId: "preview",
    status: "ready",
    webMcpToolName: "cogo.studio.renameProject",
    studioMcpToolName: "cogo.studio.renameProject",
    requiresConfirmation: true,
    description:
      "Rename the active server-backed Studio project after explicit confirmation.",
    guidance:
      "Require the exact new name. Verify local project metadata but do not claim server persistence without acknowledgement and reconnect readback.",
    inputHint: { name: "<new project name>" },
  },
  "project.metadata.update": {
    id: "project.metadata.update",
    label: "Update project metadata",
    kind: "project",
    surfaceId: "preview",
    status: "ready",
    webMcpToolName: "cogo.studio.updateProjectMeta",
    studioMcpToolName: "cogo.studio.updateProjectMeta",
    requiresConfirmation: true,
    description:
      "Update the active project's description or icon after explicit confirmation.",
    guidance:
      "Require at least one exact metadata value. Verify local project metadata but keep server persistence unverified.",
    inputHint: {
      description: "<optional description>",
      icon: "<optional icon>",
    },
  },
  "project.delete": {
    id: "project.delete",
    label: "Delete project",
    kind: "project",
    surfaceId: "preview",
    status: "ready",
    webMcpToolName: "cogo.studio.deleteProject",
    studioMcpToolName: "cogo.studio.deleteProject",
    requiresConfirmation: true,
    description:
      "Delete one server-backed Studio project by exact ID after typed confirmation.",
    guidance:
      "Show the exact project ID, require DELETE, and state that the registered tool has no automated undo. Local absence alone is not server deletion readback.",
    inputHint: { projectId: "<exact project id>" },
  },
  "project.duplicate": {
    id: "project.duplicate",
    label: "Duplicate project",
    kind: "project",
    surfaceId: "preview",
    status: "ready",
    webMcpToolName: "cogo.studio.duplicateProject",
    studioMcpToolName: "cogo.studio.duplicateProject",
    requiresConfirmation: true,
    description:
      "Duplicate the active Studio project with an optional explicit copy name.",
    guidance:
      "Confirm before creating the copy. Verify the local project list but keep server persistence unverified.",
    inputHint: { newName: "<optional copy name>" },
  },
  "release.preview.share": {
    id: "release.preview.share",
    label: "Share Coogo preview",
    kind: "publish",
    surfaceId: "publish",
    status: "ready",
    webMcpToolName: "cogo.studio.share_cogo_preview",
    studioMcpToolName: "cogo.studio.share_cogo_preview",
    requiresConfirmation: true,
    description:
      "Record a public or unlisted Coogo preview URL after the current source release passes admission.",
    guidance:
      "Require an exact preview URL and visibility. Retain returned share evidence, but do not claim live deployment or offer an undo because this tool has no revoke operation.",
    inputHint: {
      previewUrl: "<exact https preview URL>",
      visibility: "public | unlisted",
      createdBy: "<optional actor>",
    },
  },
  "release.verify": {
    id: "release.verify",
    label: "Run publication verification",
    kind: "publish",
    surfaceId: "publish",
    status: "ready",
    webMcpToolName: "cogo.studio.runPublicationVerification",
    studioMcpToolName: "cogo.studio.runPublicationVerification",
    requiresConfirmation: true,
    description:
      "Run bounded control-plane verification for one identified canonical publication release.",
    guidance:
      "Require the exact release ID and current allow-action preflight. The tool may observe evidence but cannot inject a passing result; unavailable axes remain not-proven.",
    inputHint: {
      releaseId: "<exact canonical release id>",
      projectId: "<optional exact saved project id>",
    },
  },
  "release.deploy": {
    id: "release.deploy",
    label: "Deploy publication release",
    kind: "deploy",
    surfaceId: "deploy",
    status: "ready",
    webMcpToolName: "cogo.studio.deployPublicationRelease",
    studioMcpToolName: "cogo.studio.deployPublicationRelease",
    requiresConfirmation: true,
    description:
      "Deploy one exact candidate and output hash to staging or production using a matching one-time execution authority.",
    guidance:
      "Never translate a generic deploy request into execution. First resolve the release, environment, adapter, candidate/output hashes, one-time authority, and production promotion hash when applicable; then require typed confirmation. A successful request still needs receipt and live-server readback.",
    inputHint: {
      releaseId: "<exact canonical release id>",
      environment: "staging | production",
      adapterId: "<matching registered adapter>",
      confirmation: {
        confirmed: true,
        candidateId: "<exact candidate id>",
        outputHash: "<exact output hash>",
        promotionHash: "<required for production>",
      },
    },
  },
  "release.readback": {
    id: "release.readback",
    label: "Refresh publication check",
    kind: "deploy",
    surfaceId: "deploy",
    status: "ready",
    webMcpToolName: "cogo.studio.refreshPublicationReadback",
    studioMcpToolName: "cogo.studio.refreshPublicationReadback",
    requiresConfirmation: true,
    description:
      "Refresh deployment artifact and live-server observations for one identified canonical release.",
    guidance:
      "Require the exact release ID and confirmation because refresh records new control-plane observations. Report only returned receipt and live-server hashes; a successful HTTP call alone is not proof that the release is live.",
    inputHint: {
      releaseId: "<exact canonical release id>",
      projectId: "<optional exact saved project id>",
    },
  },
  "release.rollback": {
    id: "release.rollback",
    label: "Rollback publication receipt",
    kind: "deploy",
    surfaceId: "deploy",
    status: "ready",
    webMcpToolName: "cogo.studio.rollbackPublicationReceipt",
    studioMcpToolName: "cogo.studio.rollbackPublicationReceipt",
    requiresConfirmation: true,
    description:
      "Roll an identified release back to an exact verified same-environment deployment receipt.",
    guidance:
      "Resolve the server-authorized rollback target first. Require the exact receipt ref/hash, candidate ID, target output hash, and typed confirmation; arbitrary version names are rejected. Retain the rollback receipt and refresh live readback before claiming completion.",
    inputHint: {
      releaseId: "<exact canonical release id>",
      targetDeploymentReceiptRef: {
        ref: "<exact receipt ref>",
        contentHash: "<exact sha256 receipt hash>",
      },
      confirmation: {
        confirmed: true,
        candidateId: "<exact candidate id>",
        outputHash: "<exact target output hash>",
        targetDeploymentReceiptRef: "<same exact receipt ref and hash>",
      },
    },
  },
  "project.publish.confirm": {
    id: "project.publish.confirm",
    label: "Publish project",
    kind: "publish",
    surfaceId: "deploy",
    status: "ready",
    webMcpToolName: "cogo.studio.publishProject",
    studioMcpToolName: "cogo.studio.publishProject",
    requiresConfirmation: true,
    description:
      "Publish the current project through Studio MCP after explicit chat confirmation.",
    guidance:
      "Never publish from an inferred prompt alone. Ask for confirmation, then execute cogo.studio.publishProject.",
  },
} satisfies Record<StudioOperationId, StudioOperationLegacyFields>;

const OPERATION_IDS = Object.keys(LEGACY_OPERATIONS) as StudioOperationId[];

const OPERATIONS = Object.fromEntries(
  OPERATION_IDS.map((operationId) => [
    operationId,
    buildStudioOperationV2(
      LEGACY_OPERATIONS[operationId],
      STUDIO_OPERATION_V2_PROFILES[operationId],
    ),
  ]),
) as Record<StudioOperationId, StudioOperationV2>;

export function getStudioChatOperationContract(
  operationId: string | undefined,
): ChatOperationContract | undefined {
  if (!operationId) return undefined;
  const operation = OPERATIONS[operationId as StudioOperationId];
  return operation ? { ...operation } : undefined;
}

export function listStudioChatOperationContracts(): ChatOperationContract[] {
  return Object.values(OPERATIONS).map((operation) => ({ ...operation }));
}

export function findStudioChatOperationsByToolName(
  toolName: string,
): ChatOperationContract[] {
  return Object.values(OPERATIONS)
    .filter((operation) => operation.executeTool.name === toolName)
    .map((operation) => ({ ...operation }));
}

export function formatStudioChatOperationInstructions(
  operation: ChatOperationContract,
  contextFrame?: StudioContextFrame,
): string {
  return [
    "[Studio WebMCP operation]",
    `operation.id: ${operation.id}`,
    `operation.label: ${operation.label}`,
    `operation.kind: ${operation.kind}`,
    `operation.contract_version: ${operation.contractVersion}`,
    `operation.module: ${operation.moduleId}`,
    `operation.targets: ${operation.targetKinds.join(",")}`,
    operation.surfaceId ? `surface.id: ${operation.surfaceId}` : null,
    `surface.rail_item: ${operation.surface.railItemId}`,
    `surface.direct_ui: ${operation.surface.directUi}`,
    `status: ${operation.status}`,
    `availability: ${operation.availability}`,
    operation.webMcpToolName
      ? `webmcp.tool: ${operation.webMcpToolName}`
      : null,
    operation.webMcpToolName?.startsWith("cogo.studio.")
      ? "webmcp.gateway: cogo.studio.capability.executeOperation"
      : null,
    operation.webMcpToolName?.startsWith("cogo.studio.")
      ? `webmcp.gateway_input: ${JSON.stringify({ operationId: operation.id, params: "<validated operation params>" })}`
      : null,
    operation.studioMcpToolName
      ? `studio_mcp.tool: ${operation.studioMcpToolName}`
      : null,
    operation.requiredWebMcpToolName
      ? `required_webmcp.tool: ${operation.requiredWebMcpToolName}`
      : null,
    `read_only: ${operation.readOnly === true ? "true" : "false"}`,
    `risk: ${operation.risk}`,
    `confirmation_required: ${operation.requiresConfirmation === true ? "true" : "false"}`,
    `confirmation: ${operation.confirmation}`,
    `input_schema: ${JSON.stringify(operation.inputSchema)}`,
    `readback: ${operation.readback.mode} - ${operation.readback.description}`,
    `undo: ${operation.undo.mode} - ${operation.undo.description}`,
    contextFrame
      ? `context.active_surface: ${contextFrame.activeSurface}`
      : null,
    contextFrame?.target
      ? `context.target: ${JSON.stringify(contextFrame.target)}`
      : "context.target: unresolved - ask the user or inspect the active surface before execution",
    `description: ${operation.description}`,
    operation.webMcpToolName?.startsWith("cogo.studio.")
      ? "execution_policy: Call the capability gateway, not the executor tool directly. The gateway enforces confirmation, readback, and undo."
      : null,
    operation.inputHint
      ? `input_hint: ${JSON.stringify(operation.inputHint)}`
      : null,
    operation.guidance ? `guidance: ${operation.guidance}` : null,
  ]
    .filter(Boolean)
    .join("\n");
}

export function resolveStudioOperationTarget(
  operation: ChatOperationContract,
  context: Partial<ChatContext>,
): StudioTargetRef | undefined {
  for (const targetKind of operation.targetKinds) {
    switch (targetKind) {
      case "app":
        if (context.projectId) {
          return { kind: "app", projectId: context.projectId };
        }
        break;
      case "page":
        if (context.pageId) {
          return { kind: "page", pageId: context.pageId };
        }
        break;
      case "element":
        if (
          context.pageId &&
          context.selectedBlockName &&
          context.selectedElementId
        ) {
          return {
            kind: "element",
            pageId: context.pageId,
            blockName: context.selectedBlockName,
            elementId: context.selectedElementId,
          };
        }
        break;
      case "flow":
        if (context.flowId) {
          return {
            kind: "flow",
            flowId: context.flowId,
            ...(context.selectedBlockName
              ? { blockName: context.selectedBlockName }
              : {}),
          };
        }
        break;
      case "datasource":
        if (context.selectedBlockName && context.dataSourceName) {
          return {
            kind: "datasource",
            blockName: context.selectedBlockName,
            sourceName: context.dataSourceName,
          };
        }
        break;
      case "data-binding":
        if (context.selectedBlockName && context.selectedElementId) {
          return {
            kind: "data-binding",
            blockName: context.selectedBlockName,
            elementId: context.selectedElementId,
          };
        }
        break;
      case "figma-source":
        if (context.sourceWorkspaceId) {
          return {
            kind: "figma-source",
            workspaceId: context.sourceWorkspaceId,
            ...(context.sourceRevision !== undefined
              ? { sourceRevision: context.sourceRevision }
              : {}),
            ...(context.sourceNodeId ? { nodeId: context.sourceNodeId } : {}),
          };
        }
        break;
      case "document":
        if (context.documentId) {
          return { kind: "document", documentId: context.documentId };
        }
        break;
      case "document-page":
        if (context.documentId && context.documentPageId) {
          return {
            kind: "document-page",
            documentId: context.documentId,
            pageId: context.documentPageId,
          };
        }
        break;
      case "document-node":
        if (context.documentId && context.documentNodeId) {
          return {
            kind: "document-node",
            documentId: context.documentId,
            nodeId: context.documentNodeId,
          };
        }
        break;
      case "document-proposal":
        if (context.documentId && context.documentProposalId) {
          return {
            kind: "document-proposal",
            documentId: context.documentId,
            proposalId: context.documentProposalId,
          };
        }
        break;
      case "report":
        if (context.reportId) {
          return { kind: "report", reportId: context.reportId };
        }
        break;
      case "report-section":
        if (context.reportId && context.reportSectionId) {
          return {
            kind: "report-section",
            reportId: context.reportId,
            sectionId: context.reportSectionId,
          };
        }
        break;
      case "export-target":
        if (
          context.documentTarget === "docx" ||
          context.documentTarget === "hwpx" ||
          context.documentTarget === "pptx" ||
          context.documentTarget === "pdf" ||
          context.documentTarget === "xlsx"
        ) {
          return { kind: "export-target", target: context.documentTarget };
        }
        break;
      case "release":
        if (context.projectId) {
          return { kind: "release", projectId: context.projectId };
        }
        break;
    }
  }
  return undefined;
}

export function buildStudioChatOperationContext(
  operationId: string | undefined,
  context: Partial<ChatContext> = {},
): Pick<
  ChatContext,
  | "operationContract"
  | "webMcpToolName"
  | "studioMcpToolName"
  | "webMcpToolStatus"
  | "chatOperationInstructions"
  | "operationContextFrame"
> {
  const operation = getStudioChatOperationContract(operationId);
  if (!operation) return {};
  const operationContextFrame: StudioContextFrame = {
    projectId: context.projectId,
    activeSurface: operation.surface.railItemId,
    target: resolveStudioOperationTarget(operation, context),
    sourceRevision: context.sourceRevision,
  };
  return {
    operationContract: operation,
    operationContextFrame,
    ...(operation.webMcpToolName
      ? { webMcpToolName: operation.webMcpToolName }
      : {}),
    ...(operation.studioMcpToolName
      ? { studioMcpToolName: operation.studioMcpToolName }
      : {}),
    webMcpToolStatus: operation.status,
    chatOperationInstructions: formatStudioChatOperationInstructions(
      operation,
      operationContextFrame,
    ),
  };
}
